import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FaSearch, FaFileDownload, FaSpinner, FaCalendar, FaClock,
  FaEye, FaUser, FaMoneyBillWave, FaIdCard, FaCheckCircle,
  FaTimesCircle, FaArrowLeft, FaArrowRight, FaFilter, FaSync,
  FaExclamationTriangle, FaInfoCircle, FaTag, FaBuilding,
  FaUsers, FaChartBar
} from "react-icons/fa";

const WithdrawalHistory = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showTodayOnly, setShowTodayOnly] = useState(false);
  const [totalRequests, setTotalRequests] = useState(0);
  const [totalWithdrawn, setTotalWithdrawn] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Function to filter data between start and end dates
  const filterDataByDateRange = () => {
    if (!startDate || !endDate) {
      alert("Please select both start and end dates");
      return;
    }
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) {
      alert("Start date cannot be after end date");
      return;
    }
  };

  // Reset date filters
  const clearDateFilters = () => {
    setStartDate("");
    setEndDate("");
    setShowTodayOnly(false);
  };

  // Handle Today checkbox
  const handleTodayCheckbox = (e) => {
    const isChecked = e.target.checked;
    setShowTodayOnly(isChecked);

    if (isChecked) {
      // Set today's date for both start and end using local timezone
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`; // Format: YYYY-MM-DD
      setStartDate(formattedDate);
      setEndDate(formattedDate);
    } else {
      // Clear the dates if unchecked
      setStartDate("");
      setEndDate("");
    }
  };

  // Handle view details modal
  const handleViewDetails = (withdrawal) => {
    setSelectedRequest(withdrawal);
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedRequest(null);
  };

  // Fetch withdrawal history on component mount
  useEffect(() => {
    const fetchWithdrawalHistory = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/admin/get/reporters/withdrawl/history`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Withdrawal history response:", response);
        // The correct array is response.data.data.withdrawals
        const data = response.data.data || {};
        setWithdrawals(Array.isArray(data.withdrawals) ? data.withdrawals : []);
        setTotalRequests(data.totalRequests || 0);
        setTotalWithdrawn(data.totalWithdrawn || 0);
      } catch (error) {
        setError("Error fetching withdrawal history.");
      } finally {
        setLoading(false);
      }
    };
    fetchWithdrawalHistory();
  }, []);

  // Filter withdrawals by ID, user name, amount, IINSAF ID, or date
  const filteredWithdrawals = withdrawals.filter((withdrawal) => {
    // Add null checks for withdrawal object
    if (!withdrawal) return false;

    const id = withdrawal._id?.toLowerCase() || "";
    const userName = withdrawal.reporterId?.name?.toLowerCase() || "";
    const iinsafId = withdrawal.reporterId?.iinsafId?.toLowerCase() || "";
    const amount = withdrawal.amount?.toString() || "";
    const date = withdrawal.createdAt
      ? new Date(withdrawal.createdAt).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit'
      }).toLowerCase()
      : "";
    const search = searchTerm.toLowerCase();

    // Apply text search filter
    const matchesSearch =
      id.includes(search) ||
      userName.includes(search) ||
      iinsafId.includes(search) ||
      amount.includes(search) ||
      date.includes(search);

    // Apply date range filter
    const createdAt = withdrawal.createdAt
      ? new Date(withdrawal.createdAt)
      : null;

    // Create new date objects to avoid mutating the original dates
    const filterStartDate = startDate ? new Date(startDate + 'T00:00:00') : null;
    const filterEndDate = endDate ? new Date(endDate + 'T23:59:59.999') : null;

    const withinDateRange =
      (!filterStartDate || (createdAt && createdAt >= filterStartDate)) &&
      (!filterEndDate || (createdAt && createdAt <= filterEndDate));

    return matchesSearch && withinDateRange;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredWithdrawals.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentWithdrawals = filteredWithdrawals.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, startDate, endDate, showTodayOnly]);

  // Pagination handlers
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-6">
      {/* Header Section */}
      <div className="bg-white rounded-2xl shadow-xl mb-6 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-500 to-indigo-500 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                <FaMoneyBillWave className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Withdrawal History</h1>
                <p className="text-blue-100">Track all withdrawal transactions and their status</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-white">
                {filteredWithdrawals.length}
              </div>
              <div className="text-blue-100">Total Withdrawals</div>
            </div>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="p-6 bg-gray-50">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by Payment ID, Name, IINSAF ID, Amount..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Date Range Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex items-center space-x-2">
                <FaCalendar className="text-blue-500 w-4 h-4" />
                <input
                  type="date"
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setShowTodayOnly(false);
                  }}
                  max={endDate || undefined}
                  disabled={showTodayOnly}
                />
              </div>

              <div className="flex items-center space-x-2">
                <span className="text-gray-400">to</span>
                <input
                  type="date"
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setShowTodayOnly(false);
                  }}
                  min={startDate || undefined}
                  disabled={showTodayOnly}
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  id="today-checkbox"
                  type="checkbox"
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  checked={showTodayOnly}
                  onChange={handleTodayCheckbox}
                />
                <label htmlFor="today-checkbox" className="text-sm font-medium text-gray-700">
                  Today Only
                </label>
              </div>

              <button
                onClick={filterDataByDateRange}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50"
                disabled={!startDate || !endDate}
              >
                <FaSearch className="w-4 h-4" />
                <span>Filter</span>
              </button>

              {(startDate || endDate) && (
                <button
                  onClick={clearDateFilters}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all duration-200"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Filter Status */}
          {startDate && endDate && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <FaInfoCircle className="text-blue-500 w-4 h-4" />
                <span className="text-blue-700 font-medium">
                  {showTodayOnly
                    ? "Showing today's withdrawals only"
                    : `Showing withdrawals between ${new Date(startDate).toLocaleDateString()} and ${new Date(endDate).toLocaleDateString()}`
                  }
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-xl p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Requests</p>
              <p className="text-2xl font-bold text-blue-600">{totalRequests}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <FaUsers className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Withdrawn</p>
              <p className="text-2xl font-bold text-green-600">₹{totalWithdrawn?.toLocaleString()}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <FaMoneyBillWave className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Filtered Results</p>
              <p className="text-2xl font-bold text-purple-600">{filteredWithdrawals.length}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <FaChartBar className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center p-12">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <FaSpinner className="w-6 h-6 text-blue-500 animate-pulse" />
              </div>
            </div>
            <p className="mt-4 text-gray-600 font-medium">Loading withdrawal history...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center p-12">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <FaExclamationTriangle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Data</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all duration-200 flex items-center space-x-2"
            >
              <FaSync className="w-4 h-4" />
              <span>Try Again</span>
            </button>
          </div>
        ) : filteredWithdrawals.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <FaMoneyBillWave className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Withdrawals Found</h3>
            <p className="text-gray-600 text-center">
              {searchTerm || startDate || endDate
                ? "No withdrawals match your current filters. Try adjusting your search criteria."
                : "There are no withdrawal records to display at this time."
              }
            </p>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gradient-to-r from-blue-500 to-indigo-500">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <FaIdCard className="w-4 h-4" />
                        IINSAF ID
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <FaIdCard className="w-4 h-4" />
                        Payment ID
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <FaUser className="w-4 h-4" />
                        User
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <FaMoneyBillWave className="w-4 h-4" />
                        Amount
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <FaCalendar className="w-4 h-4" />
                        Date
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <FaCheckCircle className="w-4 h-4" />
                        Status
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <FaEye className="w-4 h-4" />
                        Actions
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentWithdrawals.map((withdrawal) => (
                    <tr key={withdrawal._id} className="hover:bg-gray-50 transition-colors duration-200">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                            {withdrawal.reporterId?.iinsafId?.charAt(0) || 'U'}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900 font-mono">
                              {withdrawal.reporterId?.iinsafId || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 font-mono">
                          {withdrawal._id || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {withdrawal.reporterId?.name || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-lg font-bold text-green-600">
                          ₹{withdrawal.amount?.toLocaleString() || '0'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center space-x-2">
                          <FaClock className="w-4 h-4 text-gray-400" />
                          <span>
                            {withdrawal.createdAt ? new Date(withdrawal.createdAt).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: '2-digit'
                            }) : '-'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${withdrawal.status === "approved"
                            ? "bg-green-100 text-green-800"
                            : withdrawal.status === "pending"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                          }`}>
                          {withdrawal.status || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleViewDetails(withdrawal)}
                          className="px-3 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all duration-200 flex items-center space-x-1"
                        >
                          <FaEye className="w-3 h-3" />
                          <span>View</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden p-4 space-y-4">
              {currentWithdrawals.map((withdrawal) => (
                <div key={withdrawal._id} className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-bold">
                        {withdrawal.reporterId?.iinsafId?.charAt(0) || 'U'}
                      </div>
                      <div>
                        <div className="font-bold text-gray-900">{withdrawal.reporterId?.name || 'N/A'}</div>
                        <div className="text-sm text-gray-600 font-mono">{withdrawal.reporterId?.iinsafId || 'N/A'}</div>
                        <div className="text-xs text-gray-500 font-mono">{withdrawal._id || 'N/A'}</div>
                      </div>
                    </div>
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${withdrawal.status === "approved"
                        ? "bg-green-100 text-green-800"
                        : withdrawal.status === "pending"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                      }`}>
                      {withdrawal.status || 'Unknown'}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="text-sm text-gray-600">Amount</div>
                      <div className="text-lg font-bold text-green-600">₹{withdrawal.amount?.toLocaleString() || '0'}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Date</div>
                      <div className="text-sm font-medium text-gray-900">
                        {withdrawal.createdAt ? new Date(withdrawal.createdAt).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: '2-digit'
                        }) : '-'}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => handleViewDetails(withdrawal)}
                    className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all duration-200 flex items-center justify-center space-x-2"
                  >
                    <FaEye className="w-4 h-4" />
                    <span>View Details</span>
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Pagination */}
        {!loading && !error && filteredWithdrawals.length > 0 && totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing <span className="font-medium">{startIndex + 1}</span> to{" "}
                <span className="font-medium">
                  {Math.min(endIndex, filteredWithdrawals.length)}
                </span>{" "}
                of <span className="font-medium">{filteredWithdrawals.length}</span> results
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={handlePrevious}
                  disabled={currentPage === 1}
                  className={`px-3 py-2 rounded-lg border transition-all duration-200 flex items-center space-x-1 ${currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                      : "bg-white text-gray-700 hover:bg-blue-50 hover:border-blue-300 border-gray-300"
                    }`}
                >
                  <FaArrowLeft className="w-4 h-4" />
                  <span>Previous</span>
                </button>

                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNumber = i + 1;
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => handlePageChange(pageNumber)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${currentPage === pageNumber
                            ? "bg-blue-500 text-white"
                            : "bg-white text-gray-700 hover:bg-blue-50 border border-gray-300"
                          }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={handleNext}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-2 rounded-lg border transition-all duration-200 flex items-center space-x-1 ${currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                      : "bg-white text-gray-700 hover:bg-blue-50 hover:border-blue-300 border-gray-300"
                    }`}
                >
                  <span>Next</span>
                  <FaArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-500 to-indigo-500 p-6 rounded-t-2xl">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                    <FaMoneyBillWave className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-white">Withdrawal Details</h2>
                </div>
                <button
                  onClick={closeDetailsModal}
                  className="text-white hover:text-gray-200 transition-colors p-2 rounded-lg hover:bg-white hover:bg-opacity-20"
                >
                  <FaTimesCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Withdrawal Information */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center space-x-2">
                  <FaMoneyBillWave className="w-5 h-5 text-blue-600" />
                  <span>Withdrawal Information</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="font-medium text-gray-600">Payment ID:</span>
                    <p className="text-gray-800 break-all font-mono bg-white p-2 rounded-lg mt-1">{selectedRequest._id}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Amount:</span>
                    <p className="font-bold text-green-600 text-xl mt-1">₹{selectedRequest.amount?.toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Status:</span>
                    <div className="mt-1">
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${selectedRequest.status === "approved"
                          ? "bg-green-100 text-green-800"
                          : selectedRequest.status === "pending"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-800"
                        }`}>
                        {selectedRequest.status}
                      </span>
                    </div>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Request Date:</span>
                    <p className="text-gray-800 mt-1">
                      {selectedRequest.createdAt
                        ? new Date(selectedRequest.createdAt).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                        : '-'
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* User Information */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center space-x-2">
                  <FaUser className="w-5 h-5 text-green-600" />
                  <span>User Information</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="font-medium text-gray-600">Name:</span>
                    <p className="text-gray-800 mt-1">{selectedRequest.reporterId?.name || '-'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">IINSAF ID:</span>
                    <p className="text-gray-800 font-mono mt-1">{selectedRequest.reporterId?.iinsafId || '-'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Email:</span>
                    <p className="text-gray-800 break-all mt-1">{selectedRequest.reporterId?.email || '-'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">Mobile:</span>
                    <p className="text-gray-800 mt-1">{selectedRequest.reporterId?.mobile || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Bank Details */}
              {selectedRequest.bankDetails && (
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center space-x-2">
                    <FaBuilding className="w-5 h-5 text-purple-600" />
                    <span>Bank Details</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="font-medium text-gray-600">Account Holder:</span>
                      <p className="text-gray-800 mt-1">{selectedRequest.bankDetails.accountHolder || '-'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Account Number:</span>
                      <p className="text-gray-800 font-mono mt-1">{selectedRequest.bankDetails.accountNumber || '-'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">IFSC Code:</span>
                      <p className="text-gray-800 font-mono mt-1">{selectedRequest.bankDetails.ifscCode || '-'}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Bank Name:</span>
                      <p className="text-gray-800 mt-1">{selectedRequest.bankDetails.bankName || '-'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
              <button
                onClick={closeDetailsModal}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all duration-200 flex items-center space-x-2"
              >
                <FaTimesCircle className="w-4 h-4" />
                <span>Close</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawalHistory;
